import React from 'react';
import { Creatable, TextInput } from 'ui-toolkit';
import generateEndorsementsOptions from '../utilities/generateEndorsementsOptions';
import { createOnChangeHandlerGenerator } from '../utilities/createOnChangeHandlerGenerator';
import { CdLabelCell } from '../CdLabelCell.styles';
import IHasItemDataProps from './IHasItemDataProps';
import IHasItemEventProps from './IHasItemEventProps';
import IItemProps from './IItemProps';
import SharedItemCells from './SharedItemCells';
import { useApplicationContext } from '../context/useApplicationContext';
import { IEndorsementOtherViewItem, IEndorsementViewItem } from '../../../ui-toolkit/cdItems/interfaces/ICDViewItem';
import { generateDefaultViewItem } from '../../../ui-toolkit/cdItems/data/generateDefaultViewItem';
import { eUSAState } from '../../../ui-toolkit/enums/states';
import { eCDV3ItemTypes } from '../../../ui-toolkit/enums/eCDV3ItemTypes';

interface IEndorsement
  extends IItemProps,
    IHasItemDataProps<IEndorsementViewItem | IEndorsementOtherViewItem>,
    Required<IHasItemEventProps<IEndorsementViewItem | IEndorsementOtherViewItem>> {}

export const Endorsement: React.FC<IEndorsement> = (props) => {
  const [{ order }] = useApplicationContext();

  const { data, onChange, hasEditPermissions } = props;
  const state = order ? order.property.state : undefined;
  const endorsementsOptions = generateEndorsementsOptions(state as eUSAState | undefined);

  const selectedValue =
    data.type === eCDV3ItemTypes.endorsement
      ? endorsementsOptions.find((option) => option.value === data.endorsement)
      : !!data.label
        ? {
            label: data.label,
            value: data.label,
          }
        : null;
  const isSubtypeLocked = !data.new && !!selectedValue;
  const generateDefaultOnChangeHandler = createOnChangeHandlerGenerator(data, onChange);
  const payeeEvent = generateDefaultOnChangeHandler('payee');
  const onOptionSelected = (option?: { label: string; value: IEndorsement | string; __isNew__?: boolean } | null) => {
    if (option && option.value) {
      if (option.__isNew__) {
        // FYI: always set to endorsement_other because string option means custom/other
        onChange({
          ...generateDefaultViewItem(),
          ...data,
          dirty: true,
          label: option.value as any,
          type: eCDV3ItemTypes.endorsement_other,
        });
      } else {
        // FYI: always set to endorsement because option as object means enum/sub_type
        onChange({
          ...generateDefaultViewItem(),
          ...data,
          dirty: true,
          endorsement: option.value as any,
          type: eCDV3ItemTypes.endorsement,
          overridden: false,
        });
      }
    } else {
      onChange({
        ...generateDefaultViewItem(),
        ...data,
        dirty: true,
        label: undefined,
        type: eCDV3ItemTypes.endorsement_other,
      });
    }
  };

  return (
    <SharedItemCells {...props} readOnly={!hasEditPermissions}>
      <CdLabelCell gridTemplateColumns={isSubtypeLocked ? undefined : '1fr min-content 1fr'}>
        {isSubtypeLocked ? (
          <span>{`${selectedValue!.label}`}</span>
        ) : (
          <Creatable
            className="label"
            options={endorsementsOptions}
            value={selectedValue}
            onChange={onOptionSelected}
            placeholder="Title - Endorsement"
            isDisabled={!hasEditPermissions}
          />
        )}
        <span>to</span>
        <TextInput
          onChange={payeeEvent}
          placeholder="Payee"
          value={data.payee}
          readOnly={!hasEditPermissions}
          debounceOnValueChange
        />
      </CdLabelCell>
    </SharedItemCells>
  );
};
